import React, { useEffect, useRef } from "react";
import "./c-dropdown.scss";

interface DropdownProps {
  onClickOutsideDropdown?: () => void;
  visible: boolean;
  onlyRenderVisible?: boolean;
}

export const Dropdown: React.FC<DropdownProps> = ({
  onClickOutsideDropdown,
  visible,
  onlyRenderVisible = false,
  children,
}) => {
  const dropdownRef = useRef<HTMLElement>(null);
  const dropdownWrapperRef = useRef<HTMLDivElement>(null);

  let currentTransitionTimeout: NodeJS.Timeout | number | null;
  const classes = {
    bodyOverlay: "-dropdown-showing",
    dropdownHideMobile: "--mobile-hide",
    dropdownHideDesktop: "--desktop-hide",
    transitionClass: "c-dropdown__mobile-transition",
  };

  useEffect(() => {
    const page = document.querySelector("body");
    if (!page) {
      return () => {};
    }

    if (visible) {
      page.addEventListener("click", handleMouseDown);
    }

    return () => {
      page.removeEventListener("click", handleMouseDown);
    };
  }, [visible]);

  const handleMouseDown = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      event.stopPropagation();
      event.preventDefault();
      if (onClickOutsideDropdown) {
        onClickOutsideDropdown();
      }
    }
  };

  const updateClassList = (action: "add" | "remove", element: HTMLElement | null, classNames: string[]) => {
    if (element) {
      element.classList[action](...classNames);
    }
  };

  const showTransition = () => {
    if (!currentTransitionTimeout) {
      updateClassList("add", dropdownWrapperRef.current, [classes.bodyOverlay]);
      updateClassList("remove", dropdownRef.current, [classes.dropdownHideMobile, classes.dropdownHideDesktop]);
      currentTransitionTimeout = setTimeout(() => {
        updateClassList("remove", dropdownRef.current, [classes.transitionClass]);
        currentTransitionTimeout = null;
      }, 50);
    }
  };

  const hideTransition = () => {
    if (!currentTransitionTimeout) {
      updateClassList("remove", dropdownWrapperRef.current, [classes.bodyOverlay]);
      updateClassList("add", dropdownRef.current, [classes.transitionClass, classes.dropdownHideDesktop]);
      currentTransitionTimeout = setTimeout(() => {
        updateClassList("add", dropdownRef.current, [classes.dropdownHideMobile]);
        currentTransitionTimeout = null;
      }, 350);
    }
  };

  useEffect(() => {
    if (visible) {
      showTransition();
    } else {
      hideTransition();
    }
  }, [visible, dropdownRef]);

  const shouldShowChildren = () => {
    if (visible) {
      return true;
    } else {
      if (onlyRenderVisible) {
        return false;
      }
    }

    return true;
  };

  return (
    <div className="c-dropdown__wrapper">
      <div ref={dropdownWrapperRef} className="c-dropdown-overlay" />
      <section ref={dropdownRef} className={`c-dropdown ${classes.transitionClass} ${classes.dropdownHideDesktop}`}>
        {shouldShowChildren() && children}
      </section>
    </div>
  );
};
